import jsPDF from "jspdf"
import { renderToStaticMarkup } from "react-dom/server"
import { SvgZoeeGradientLogo } from "../../../../components/icons/logos/zoee-gradient-logo"

async function svgToPngBase64(svgMarkup: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    const svg = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(svg)
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = img.width * 2
      canvas.height = img.height * 2
      const ctx = canvas.getContext("2d")!
      ctx.scale(2, 2)
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL("image/png"))
    }
    img.src = url
  })
}

function formatPaymentMethod(method: string | null, amount: any) {
  if (!amount || Number(amount) <= 0) return "—"

  if (!method) return "—"

  const clean = method.replace(/\s+/g, "")

  if (/^\d{4}$/.test(clean)) {
    return `•••• ${clean}`
  }

  if (/^\d{12,19}$/.test(clean)) {
    return `•••• ${clean.slice(-4)}`
  }

  if (method.toLowerCase().includes("paypal")) {
    return "PayPal"
  }

  return method
}

const generateInvoicePDF = async (
  transaction: any,
  user: { firstName: string; lastName: string; user_id: string }
) => {
  const doc = new jsPDF({ format: "letter", unit: "mm" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  // ---- LOGOS ----
  const svgMarkup = renderToStaticMarkup(<SvgZoeeGradientLogo />)
  const logoBase64 = await svgToPngBase64(svgMarkup)

  const checkSvgMarkup = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#27A745">
      <path d="M9.00039 16.2002L4.80039 12.0002L3.40039 13.4002L9.00039 19.0002L21.0004 7.0002L19.6004 5.6002L9.00039 16.2002Z"/>
    </svg>
  `
  const checkBase64 = await svgToPngBase64(checkSvgMarkup)

  // ---- HEADER BAR ----
  doc.setFillColor("#5B6EF5")
  doc.rect(0, 0, pageWidth, 32, "F")
  doc.setFontSize(20)
  doc.setTextColor("#fff")
  doc.text("INVOICE", pageWidth / 2, 20, { align: "center" })

  // ---- FROM / BILL TO ----
  doc.setFontSize(7)
  doc.setTextColor("#777")
  doc.text("FROM", 20, 48)
  doc.text("BILL TO", pageWidth - 70, 48)

  doc.setFontSize(11)
  doc.setTextColor("#000")

  const fromText = transaction.service_details
    ? `Coach ${transaction.merchant}`
    : "ZOEE"

  doc.text(fromText, 20, 55)
  doc.text(`${user.firstName} ${user.lastName}`, pageWidth - 70, 55)

  doc.setFontSize(9)
  doc.setTextColor("#777")
  doc.text(`User ID: ${user.user_id}`, pageWidth - 70, 61)

  // ---- DIVIDER ----
  doc.setDrawColor("#4A55D3")
  doc.setLineWidth(0.6)
  doc.line(20, 70, pageWidth - 20, 70)

  // ---- TRANSACTION DETAILS TITLE ----
  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor("#4A55D3")
  doc.text("Transaction Details", 20, 85)
  doc.setFont("helvetica", "normal")

  // ---- DETAILS TABLE ----
  const rows = [
    ["Merchant", transaction.merchant || "Zoee"],
    ["Description", transaction.description || "—"],
    ["Date Processed", transaction.date],
    ["Amount", `$${Number(transaction.amount).toFixed(2)}`, "amount"],
    [
      "Payment Method",
      formatPaymentMethod(transaction.paymentMethod, transaction.amount),
    ],
    ["Promo Code", transaction.promo || "—"],
    [
      "Status",
      transaction.status === "scheduled" ? "Scheduled" : "Paid",
      "status",
    ],
  ]

  let y = 97
  rows.forEach(([label, value, type]) => {
    doc.setFont("helvetica", "normal")
    doc.setTextColor("#666")

    // ---- LABEL ----
    if (type === "amount") {
      doc.setFont("helvetica", "bold")
      doc.setTextColor("#4A55D3")
    }
    doc.text(label, 25, y)

    // ---- STATUS ROW ----
    if (type === "status") {
      if (value === "Paid") {
        doc.addImage(checkBase64, "PNG", 95, y - 5, 6, 6)
        doc.setFont("helvetica", "bold")
        doc.setTextColor("#27A745")
        doc.text("Paid", 103, y)
      } else {
        doc.setFont("helvetica", "bold")
        doc.setTextColor("#C79300")
        doc.text("Scheduled", 95, y)
      }
      y += 11
      return
    }

    // ---- AMOUNT ROW VALUE ----
    if (type === "amount") {
      doc.setFont("helvetica", "bold")
      doc.setTextColor("#4A55D3")
      doc.text(String(value), 95, y)
    } else {
      doc.setFont("helvetica", "normal")
      doc.setTextColor("#000")
      doc.text(String(value), 95, y)
    }

    y += 11
  })

  // ---- FOOTER ----
  const bottomMargin = 18
  const logoHeight = 8
  const logoWidth = 20
  const poweredByText = "Powered by"
  const spacing = 2

  const poweredByY = pageHeight - bottomMargin 

  doc.setFontSize(8)
  doc.setTextColor("#999")
  const textWidth = doc.getTextWidth(poweredByText)
  const totalWidth = textWidth + spacing + logoWidth
  const startX = (pageWidth - totalWidth) / 2

  doc.text(poweredByText, startX, poweredByY)

  doc.addImage(
    logoBase64,
    "PNG",
    startX + textWidth + spacing,
    poweredByY - logoHeight + 1, 
    logoWidth,
    logoHeight
  )

  doc.save(`Invoice_${transaction.id}.pdf`)
}

export default generateInvoicePDF
